import { Link } from "wouter";
import { HelpCircle, Lightbulb, ArrowRight } from "lucide-react";
import { useLocale } from "@/hooks/useLocale";
import { getNextStepsForTool } from "@/lib/workflows-data";
import { getToolById, getCategoryById } from "@/lib/tools-data";
import { FEATURED_APP_IDS } from "@/lib/featured-apps-data";
import { getToolName } from "@/lib/i18n/tool-name-translations";
import { CollapsibleText } from "@/components/CollapsibleText";
import { useRegisterToolInfo } from "@/components/ToolInfoContext";

/**
 * The two-column layout from the reference mockup: a wide left workspace
 * (the tool itself, passed as children) plus a narrow right sidebar with
 * "What this tool does", "Pro tips", and "Continue with" panels.
 *
 * Deliberately opt-in per tool, not a change to ToolPage.tsx itself --
 * only genuinely narrow-workspace tools (simple file lists, forms,
 * calculators) should use this. Tools needing a large canvas or preview
 * (image/video editors, PDF Editor, design tools) would feel cramped in
 * the ~320px sidebar's remaining width and should keep the plain
 * ToolContainer instead. See the categorization from the mockup review:
 * ~130 tools are good fits, ~100 need full width.
 *
 * whatItDoes/proTip stay as plain props (not moved into this component)
 * so each tool's own TOOL_INFO_CONTENT -- already co-located in that
 * tool's own lazy-loaded chunk for bundle-size reasons -- doesn't have to
 * move anywhere. This component just changes how that same content
 * renders, not where it's defined.
 *
 * Mobile: the grid collapses to a single column by default (no lg:
 * prefix needed on the base layout), so the sidebar panels naturally
 * stack below the workspace -- exactly how ToolInfoSection and
 * NextStepSuggestion already behave today. Nothing new to design there.
 */
/**
 * Tools using the ToolWithSidebar layout (see that component) already
 * show their own "Continue with" panel in the sidebar -- ToolPage.tsx
 * checks this set to skip rendering the separate NextStepSuggestion
 * below the tool, which would otherwise duplicate the same suggestion.
 * Add a tool's id here when it adopts the sidebar layout.
 */
export const SIDEBAR_LAYOUT_TOOL_IDS = new Set(["merge-pdf", "split-pdf", "compress-pdf", "rotate-pdf", "book-writing-toolbox"]);

export function ToolWithSidebar({
  children,
  toolId,
  categoryId,
  whatItDoes,
  proTip,
}: {
  children: React.ReactNode;
  toolId: string;
  categoryId: string;
  whatItDoes: string;
  proTip?: string;
}) {
  const { strings, locale, localizedHref } = useLocale();

  // On a tool page the sidebar text moves into the shared info panel below
  // the tool (same for every tool); the sidebar only renders as a fallback.
  const collectedByPage = useRegisterToolInfo({ whatIsIt: whatItDoes, tip: proTip });
  if (collectedByPage) return <div className="min-w-0">{children}</div>;

  // grid-cols-1 / minmax(0,1fr) rather than the implicit auto track: with no explicit column
  // below lg, the track grew to the widest child's minimum width and the page overflowed
  // sideways by ~7px at 360px wide (measured on Merge PDF).
  return (
    <div className="grid grid-cols-1 lg:grid-cols-[minmax(0,1fr)_320px] gap-8 items-start max-w-6xl mx-auto">
      <div className="min-w-0">{children}</div>

      <aside className="space-y-4 lg:sticky lg:top-6">
        <div className="card-raised p-4 rounded-xl">
          <h2 className="flex items-center gap-2 text-sm font-semibold mb-2">
            <HelpCircle className="h-4 w-4 text-primary" />
            {strings.toolPage.whatThisToolDoes}
          </h2>
          <CollapsibleText lines={2}>{whatItDoes}</CollapsibleText>
        </div>

        {proTip && (
          <div className="card-raised p-4 rounded-xl">
            <h2 className="flex items-center gap-2 text-sm font-semibold mb-2">
              <Lightbulb className="h-4 w-4 text-primary" />
              {strings.toolPage.proTips}
            </h2>
            <CollapsibleText lines={2}>{proTip}</CollapsibleText>
          </div>
        )}

        <ContinueWithPanel toolId={toolId} categoryId={categoryId} locale={locale} localizedHref={localizedHref} label={strings.toolPage.continueWith} fallbackLabel={strings.toolPage.moreFromCategory} />
      </aside>
    </div>
  );
}

function ContinueWithPanel({
  toolId,
  categoryId,
  locale,
  localizedHref,
  label,
  fallbackLabel,
}: {
  toolId: string;
  categoryId: string;
  locale: import("@/lib/i18n/locales").Locale;
  localizedHref: (path: string) => string;
  label: string;
  fallbackLabel: string;
}) {
  const suggestions = getNextStepsForTool(toolId);

  // Real workflow suggestions exist for this tool: show up to 3, each a
  // genuine "next step in a real sequence" rather than a generic list.
  if (suggestions.length > 0) {
    const items = suggestions.slice(0, 3).map(s => ({
      tool: getToolById(s.nextStep.toolId),
      note: s.nextStep.note,
    })).filter((i): i is { tool: NonNullable<typeof i.tool>; note: string } => !!i.tool);

    if (items.length > 0) {
      return (
        <div className="card-raised p-4 rounded-xl">
          <h2 className="text-sm font-semibold mb-3">{label}</h2>
          <div className="space-y-3">
            {items.map(({ tool, note }) => (
              <Link
                key={tool.id}
                href={localizedHref(`/tool/${tool.id}`)}
                className="flex items-start justify-between gap-2 group"
              >
                <div className="min-w-0">
                  <p className="text-sm font-medium group-hover:text-primary transition-colors truncate">
                    {getToolName(locale, tool.id, tool.name)}
                  </p>
                  <p className="text-xs text-muted-foreground truncate">{note}</p>
                </div>
                <ArrowRight className="h-3.5 w-3.5 text-primary shrink-0 mt-0.5 group-hover:translate-x-0.5 transition-transform" />
              </Link>
            ))}
          </div>
        </div>
      );
    }
  }

  // No workflow data for this tool -- fall back to a few other tools
  // from the same category, so the panel is never empty. Real content
  // either way, not a placeholder.
  const category = getCategoryById(categoryId);
  if (!category) return null;
  const otherTools = category.tools.filter(t => t.id !== toolId && !t.hidden && !FEATURED_APP_IDS.has(t.id)).slice(0, 3);
  if (otherTools.length === 0) return null;

  return (
    <div className="card-raised p-4 rounded-xl">
      <h2 className="text-sm font-semibold mb-3">{fallbackLabel}</h2>
      <div className="space-y-2.5">
        {otherTools.map(tool => (
          <Link
            key={tool.id}
            href={localizedHref(`/tool/${tool.id}`)}
            className="flex items-center justify-between group"
          >
            <span className="text-sm group-hover:text-primary transition-colors truncate">
              {getToolName(locale, tool.id, tool.name)}
            </span>
            <ArrowRight className="h-3.5 w-3.5 text-primary shrink-0 group-hover:translate-x-0.5 transition-transform" />
          </Link>
        ))}
      </div>
    </div>
  );
}
